import { TableFooter } from '@fukuri-admins/features/Table/TableFooter';
import { FileDownloadIcon, TableSearch } from '@fukuri-admins/ui/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { downloadFile, useClearableFilters } from '@fukuri-admins/utils/common';
import { CircularProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getServiceUsages, getServiceUsagesCSV } from '../../../api/service_usages';
import { useAuth } from '../../../components/auth/AuthProvider';
import { ContentsHeader } from '../../../components/common/ContentsHeader';
import { ServiceUsageTableContent } from '../../../components/serviceUsage/ServiceUsageTableContent';
import { ServiceUsageTableFilter } from '../../../components/serviceUsage/ServiceUsageTableFilter';
import { ServiceUsage } from '../../../types/resources/ServiceUsageType';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType';
import { handleSorting } from '../../../utils/sortings';
import styles from './styles.module.scss';

export const IndexServiceUsage = () => {
  const { signOut } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filteredServiceUsages, setFilteredServiceUsages] = useState<ServiceUsage[]>([]);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filterParams, setFilterParams] = useSearchParams();
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>({
    ...defaultActionTableSortParams,
    column: 'no',
  });

  const [outputCsvFetchDone, setOutputCsvFetchDone] = useState(false);
  const outputCsvFetchDoneRef = useRef<{ link: HTMLAnchorElement } | null>(null);
  const { showToast } = useToast();

  const serviceUsageTableColumns: ActionTableColumnsType[] = [
    { label: '会社名', key: 'user.client.name', sortable: true, filterable: true },
    {
      label: 'サービス名',
      key: 'service.title',
      sortable: true,
      filterable: true,
    },
    { label: '名前', key: 'user.full_name', sortable: true, filterable: true },
    { label: 'メール', key: 'user.email', sortable: true, filterable: true },
    {
      label: '利用日時',
      key: 'created_at',
      sortable: true,
      filterable: false,
    },
  ];

  const formatBaseFilterParams = (filterParams: URLSearchParams) => {
    const userName = filterParams.get('user[name]');
    const clientName = filterParams.get('client[name]');

    return {
      ...(userName && { 'user[name]': userName }),
      ...(clientName && { 'client[name]': clientName }),
    };
  };

  const formatFilterParamsForData = (filterParams: URLSearchParams, page?: number) => {
    const baseParams = formatBaseFilterParams(filterParams);
    const pageNumber = page ? page : filterParams.get('page');

    return {
      ...baseParams,
      ...(pageNumber && { page: pageNumber.toString() }),
    };
  };

  const handleOutputCsv = async () => {
    try {
      const { data } = await getServiceUsagesCSV(signOut, formatBaseFilterParams(filterParams));
      downloadFile(data, 'service_usages.csv');
    } catch {
      showToast({ message: 'CSVのダウンロードに失敗しました。', status: 'danger' });
    }
  };

  const getServiceUsagesData = async (page?: number) => {
    try {
      const { data, headers } = await getServiceUsages(
        signOut,
        formatFilterParamsForData(filterParams, page)
      );
      setTotalPage(Number(headers['total-pages']));
      setTotalCount(Number(headers['total-count']));
      const processedServiceUsages = data.service_usages?.map((serviceUsage: ServiceUsage) => {
        return {
          id: serviceUsage.id,
          service: serviceUsage.service,
          user: serviceUsage.user,
          created_at: serviceUsage.created_at,
        };
      });
      setFilteredServiceUsages(processedServiceUsages);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const { setIsClearFilter, filterKey } = useClearableFilters({
    filterParams,
    fetchFunction: getServiceUsagesData,
  });

  const onClear = () => {
    setIsLoading(true);
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('user[name]');
    newParams.delete('client[name]');
    setFilterParams(newParams);
    setIsClearFilter(true);
  };

  const onSearch = () => {
    setIsLoading(true);
    getServiceUsagesData();
  };

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    getServiceUsagesData(page);
  };

  useEffect(() => {
    getServiceUsagesData();
  }, []);

  useEffect(() => {
    if (outputCsvFetchDone) {
      outputCsvFetchDoneRef.current?.link.click();
      setOutputCsvFetchDone(false);
    }
  }, [outputCsvFetchDone]);

  return (
    <div className={styles.Root} data-testid='service-usages-page'>
      <ContentsHeader
        title='サービス利用履歴'
        actions={
          <div className={styles.menuContainer}>
            <div className={styles.outputCsv} onClick={handleOutputCsv}>
              <FileDownloadIcon />
              CSVダウンロード
            </div>
          </div>
        }
      />
      <div className={styles.tableContainer}>
        <div className={styles.Root__Table}>
          <div className={styles.searchContainer}>
            <TableSearch onSearch={onSearch} onClear={onClear}>
              <ServiceUsageTableFilter key={filterKey} />
            </TableSearch>
          </div>
          {isLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : (
            <>
              <ServiceUsageTableContent
                rows={filteredServiceUsages}
                columns={serviceUsageTableColumns}
                sortParams={sortParams}
                headerSort={column =>
                  handleSorting(
                    column,
                    sortParams,
                    filteredServiceUsages,
                    setSortParams,
                    setFilteredServiceUsages
                  )
                }
              />
              <TableFooter
                onPageChange={handlePageChange}
                totalCount={totalCount}
                totalPage={totalPage}
                itemCount={filteredServiceUsages.length}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
